import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RoleBasedNavigation from '../../components/ui/RoleBasedNavigation';
import { supplierService } from '../../services/supplierService';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';

const BrowseKitchens = () => {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCuisine, setSelectedCuisine] = useState('all');

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    setLoading(true);
    const { data, error } = await supplierService?.getAllSuppliers();
    
    if (error) {
      setError('Failed to load suppliers');
      console.error('Error fetching suppliers:', error);
    } else {
      setSuppliers(data || []);
    }
    setLoading(false);
  };

  const filteredSuppliers = suppliers?.filter(supplier => {
    const matchesSearch = supplier?.business_name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         supplier?.service_description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchesSearch;
  });

  const handleSupplierClick = (supplierId) => {
    navigate(`/supplier-profile/${supplierId}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-heading font-bold mb-2">Browse Kitchens</h1>
          <p className="text-muted-foreground">Discover amazing home chefs and their delicious offerings</p>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Icon 
              name="Search" 
              size={20} 
              className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" 
            />
            <input
              type="text"
              placeholder="Search by kitchen name or cuisine..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full pl-12 pr-4 py-3 rounded-lg border border-border bg-card focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Suppliers Grid */}
        {!loading && !error && (
          <>
            {filteredSuppliers?.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="ChefHat" size={32} color="var(--color-muted-foreground)" />
                </div>
                <p className="text-muted-foreground text-lg">No kitchens found</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredSuppliers?.map((supplier) => (
                  <div
                    key={supplier?.id}
                    onClick={() => handleSupplierClick(supplier?.id)}
                    className="bg-card rounded-xl overflow-hidden shadow-warm hover:shadow-warm-lg transition-smooth cursor-pointer group"
                  >
                    {/* Supplier Image */}
                    <div className="relative h-48 overflow-hidden">
                      <Image
                        src={supplier?.profile_image || 'https://images.unsplash.com/photo-1556910103-1c02745aae4d'}
                        alt={supplier?.profile_image_alt || `${supplier?.business_name} kitchen`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-smooth"
                      />
                      {supplier?.is_verified && (
                        <div className="absolute top-3 right-3 bg-primary text-primary-foreground px-3 py-1 rounded-lg text-xs font-bold flex items-center gap-1">
                          <Icon name="BadgeCheck" size={14} />
                          Verified
                        </div>
                      )}
                    </div>

                    {/* Supplier Info */}
                    <div className="p-5">
                      <h3 className="font-heading font-bold text-xl mb-2 line-clamp-1">
                        {supplier?.business_name}
                      </h3>
                      
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                        {supplier?.service_description}
                      </p>

                      {/* Stats */}
                      <div className="flex items-center justify-between pt-4 border-t border-border">
                        <div className="flex items-center gap-1">
                          <Icon name="Star" size={16} color="var(--color-warning)" className="fill-current" />
                          <span className="font-semibold">{supplier?.rating?.toFixed(1) || '0.0'}</span>
                          <span className="text-sm text-muted-foreground">({supplier?.total_reviews || 0})</span>
                        </div>
                        
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Icon name="ShoppingBag" size={16} />
                          <span>{supplier?.total_orders || 0} orders</span>
                        </div>
                      </div>

                      {/* View Button */}
                      <Button
                        variant="default"
                        className="w-full mt-4"
                        iconName="ArrowRight"
                        iconPosition="right"
                      >
                        View Kitchen
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BrowseKitchens;